import React, { RefObject, useEffect, useMemo, useRef, useState } from "react";

import { Button, ConfigProvider, Form, Modal } from "antd";
import type { FormProps } from "antd";
import { useNavigate } from "react-router";
import "./style.css";

import { filterUpdateEvent } from "@/utils/analytics/googleAnalytics";
import { objectToSearchParams } from "@/views/Results/utils/search";
import { scrollElementIntoView } from "@/utils/iframeUtils";

import ErrorHandler from "../ErrorHandler";
import { useUserOrganization } from "@/utils/partnerData";
import SearchFormSteps from "./SearchFormSteps";
import type { SearchFormProps, SearchFormStepsProps, SearchFormValues } from "./types";

function useScrolledPast(ref: RefObject<HTMLElement | null>) {
    const [isIntersecting, setIntersecting] = useState(false);

    const observer = useMemo(
        () => new IntersectionObserver(([entry]) => setIntersecting(entry.boundingClientRect.bottom < 0)),
        []
    );

    useEffect(() => {
        if (ref.current) {
            observer.observe(ref.current);
            return () => observer.disconnect();
        }
        return () => {
            /* no-op */
        };
    }, [observer, ref]);

    return isIntersecting;
}

function DemoVideo(props: { isOpen: boolean; onClose: () => void }) {
    const { isOpen, onClose } = props;

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            width="fit-content"
            closable={false}
            destroyOnHidden
            footer={
                <Button type="primary" size="large" onClick={onClose}>
                    Close
                </Button>
            }
        >
            <iframe
                src="https://player.vimeo.com/video/1054250035?h=84c36ba233&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                className="border-none demo-video"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                title="Trial Finder Intro and Demo"
            />
            <script src="https://player.vimeo.com/api/player.js" />
        </Modal>
    );
}

function scrollToStep(step: React.RefObject<HTMLDivElement | null>) {
    scrollElementIntoView(step.current, { behavior: "smooth", block: "center" });
}

function SearchForm(props: SearchFormProps) {
    const { onSubmit, initialState, form: providedForm, size, children, conditions } = props;
    const { hideSearchBar, hideDiseaseGrid, hideLocation, hideAge, hideSex, hideStudyType } = props;
    const { searchLabel, locationLabel, distanceLabel, ageLabel, sexLabel, studyTypeLabel } = props;
    const { conditionPlaceholder } = props;

    const [showDemo, setShowDemo] = useState(false);
    const [internalForm] = Form.useForm<SearchFormValues>();
    const form = providedForm || internalForm;

    const topOfMenu = useRef<HTMLDivElement>(null);

    // Scroll the user through the searching process
    // const stepOne = topOfMenu;
    const stepTwo = useRef<HTMLDivElement>(null);
    const stepThree = useRef<HTMLDivElement>(null);
    const stepFour = useRef<HTMLDivElement>(null);
    const stepFive = useRef<HTMLDivElement>(null);

    const showSticky = useScrolledPast(topOfMenu);

    const navigate = useNavigate();

    const { userOrganization } = useUserOrganization();

    const initialFormValues = useMemo<SearchFormValues>(() => {
        const hasLocation = initialState?.coordinates || initialState?.locationDisplayName;
        return {
            condition: initialState?.condition || "",
            location: hasLocation
                ? {
                      coordinates: initialState?.coordinates,
                      locationDisplayName: initialState?.locationDisplayName,
                  }
                : undefined,
            distanceInMeters: initialState?.distanceInMeters,
            age: initialState?.age,
            gender: initialState?.gender,
            studyTypes: initialState?.studyTypes || [],
        };
    }, [initialState]);

    useEffect(() => {
        form.setFieldsValue(initialFormValues);
    }, [form, initialFormValues]);

    const scrollToTop = () => {
        scrollElementIntoView(topOfMenu.current);
    };

    const handleValuesChange: FormProps<SearchFormValues>["onValuesChange"] = (changedValues) => {
        if (changedValues.distanceInMeters !== undefined) {
            filterUpdateEvent("distance", String(changedValues.distanceInMeters));
            scrollToStep(stepThree);
        }
        if (changedValues.age) {
            filterUpdateEvent("age", changedValues.age);
        }
        if (changedValues.gender) {
            filterUpdateEvent("gender", changedValues.gender);
        }
        if (changedValues.studyTypes) {
            const studyTypesValue = [...changedValues.studyTypes].sort().join(",");
            filterUpdateEvent("studyTypes", studyTypesValue);
            scrollToStep(stepFive);
        }
    };

    const handleFinish: FormProps<SearchFormValues>["onFinish"] = (values) => {
        const urlParams = objectToSearchParams({
            condition: values.condition,
            age: values.age,
            gender: values.gender,
            distanceInMeters: values.distanceInMeters,
            studyTypes: values.studyTypes,
            coordinates: values.location?.coordinates,
            locationDisplayName: values.location?.locationDisplayName,
            subset: values.subset,
        });

        navigate({ pathname: "/results", search: urlParams.toString() });

        if (onSubmit !== undefined) {
            onSubmit();
        }
    };

    const stepsProps: SearchFormStepsProps = {
        form,
        initialState,
        labels: { searchLabel, locationLabel, distanceLabel, ageLabel, sexLabel, studyTypeLabel },
        placeholders: { conditionPlaceholder },
        hide: { hideSearchBar, hideDiseaseGrid, hideLocation, hideAge, hideSex, hideStudyType },
        stepRefs: {
            topOfMenu,
            stepTwo,
            stepThree,
            stepFour,
            stepFive,
        },
        showSticky,
        conditions,
        scrollToTop,
        scrollToStep,
        onOpenDemo: () => setShowDemo(true),
    };

    const content =
        typeof children === "function" ? (
            children(stepsProps)
        ) : children !== undefined ? (
            children
        ) : (
            <SearchFormSteps {...stepsProps} />
        );

    return (
        <ConfigProvider
            theme={{
                token: {
                    fontSize: 24,
                    controlHeight: 48,
                },
                components: {
                    Button: {
                        contentFontSize: 20,
                        borderRadius: 4,
                        paddingBlock: 20,
                        paddingInline: 18,
                    },
                },
            }}
        >
            <ErrorHandler>
                <DemoVideo isOpen={showDemo} onClose={() => setShowDemo(false)} />
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={initialFormValues}
                    onFinish={handleFinish}
                    onValuesChange={handleValuesChange}
                    size={size}
                >
                    <div className="select-params-container">{content}</div>
                </Form>
            </ErrorHandler>
        </ConfigProvider>
    );
}

SearchForm.defaultProps = {
    onSubmit: () => {
        /* noop */
    },
    initialState: {},
};

export default SearchForm;

export { default as SearchFormSteps } from "./SearchFormSteps";
export type {
    ComponentLabel,
    InputPlaceholder,
    LocationFormValue,
    SearchFormChildren,
    SearchFormProps,
    SearchFormStepsProps,
    SearchFormValues,
    SearchFormStepRefs,
    ShowComponents,
} from "./types";
